import { useQuery } from "@tanstack/react-query";
import { api } from "../../app/http/axiosClient";

export function useEventsList() {
  return useQuery({
    queryKey: ["dashboard", "events"],
    queryFn: async ({ signal }) => {
      const response = await api.get("/dashboard/events",
        { signal }
      );

      return response.data.events ?? [];
    },
  });
}

export function useOrganiser() {
  return useQuery({
    queryKey: ["dashboard", "organiser"],
    queryFn: async ({ signal }) => {
      const response = await api.get("/dashboard/organiser",
        { signal }
      );

      return response.data;
    },
  });
}

export function useEventDetails(eventId) {
  return useQuery({
    queryKey: ["dashboard", "event", eventId],

    queryFn: async ({ signal }) => {
      const response = await api.get(
        `/dashboard/events/${eventId}`,
        { signal }
      );

      return response.data;
    },

    enabled: eventId != null,
  });
}

export async function getEventOverview(eventId, { metric, duration } = {}) {
  const res = await api.get(`/dashboard/events/${eventId}/overview`,
    {
      params: { metric, duration },
    });

  return res.data;
}

export async function getEventOverviewTimeline(eventId, { metric, duration } = {}) {
  const res = await api.get(`/dashboard/events/${eventId}/overview/timeline`,
    {
      params: { metric, duration },
    });

  return res.data;
}

export function useEventOrders(
  eventId,
  {
    page = 1,
    limit = 25,
    latestOrderId = null,
    search = "",
    status = [],
    sort = "desc",
    requestKey = 0,
  } = {},
) {
  return useQuery({
    queryKey: [
      "dashboard",
      "events",
      eventId,
      "orders",
      {
        page,
        limit,
        latestOrderId,
        search,
        status,
        sort,
        requestKey,
      },
    ],

    queryFn: async ({ signal }) => {
      const response = await api.get(
        `/dashboard/events/${eventId}/orders`,
        {
          signal,
          params: {
            page,
            limit,
            latestOrderId,
            search,
            status,
            sort,
          },
          paramsSerializer: {
            indexes: null,
          },
        },
      );

      return response.data;
    },

    enabled: Boolean(eventId),

    retry: (failureCount, error) => {
      if (error.response?.status === 403) {
        return false;
      }

      return failureCount < 2;
    },
  });
}

export async function getOrderItems(eventId, orderId) {
  const res = await api.get(`/dashboard/events/${eventId}/orders/${orderId}`);
  return res.data.items;
}

export async function getGuestList(eventId) {
  const response = await api.get(
    `/dashboard/events/${eventId}/guest-list/pdf`,
    {
      responseType: "blob",
    }
  );
  const contentDisposition = response.headers["content-disposition"];
  const filenameMatch = contentDisposition?.match(
    /filename="?([^"]+)"?/
  );
  const filename = filenameMatch?.[1] ?? "Guest_List.pdf";
  const pdfUrl = window.URL.createObjectURL(response.data);
  const link = document.createElement("a");
  link.href = pdfUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(pdfUrl);
}

export async function copyEventLink(slug) {
  if (!slug) {
    throw new Error("Event slug is required");
  }
  const eventUrl = `https://tickets.npsolved.com/events/${slug}`;
  await navigator.clipboard.writeText(eventUrl);
  return eventUrl;
}